import { useCallback, useEffect, useMemo, useState } from 'react'
import { getUnpackedSuggestions } from './utils'

/**
 * @template Item
 * @param {UseFilteredDropdownListProps<Item>} param0
 * @param {boolean=} multiple
 */
export default function useFilteredDropdownList (
  {
    data,
    defaultSelectedItemsIndices,
    disabledItemsIndices,
    filter,
    getGroupChildren,
    getItemValue,
    onSelectedItemsChanged,
    renderGroupLabel,
    renderItem,
    selectedItemsIndices: propsSelectedItemsIndices,
    withGrouping
  },
  multiple
) {
  const getSelectedIndices = multiple ? getMultipleSelectedIndices : getSelectedIndex
  const [selectedItemsIndices, setSelectedItemsIndices] = useState(
    defaultSelectedItemsIndices || []
  )
  const indexedSuggestions = useMemo(
    () =>
      getIndexedUnpackedSuggestions(
        getUnpackedSuggestions(
          data,
          withGrouping,
          getGroupChildren,
          renderGroupLabel
        ),
        withGrouping
      ),
    [data, withGrouping, getGroupChildren, renderGroupLabel]
  )
  /**
   * @type {IndexedGroupItem<Item>[] | IndexedItem<Item>[]}
   */
  const suggestions = useMemo(() => {
    if (filter) {
      return withGrouping
        ? indexedSuggestions.reduce(groupReducer, [])
        : indexedSuggestions.filter(itemFilter, filter)
    }
    return indexedSuggestions

    /**
     * @this {string} Filter text
     * @param {IndexedItem<Item>} param0
     * @returns {boolean}
     */
    function itemFilter ({ value }) {
      return (
        getItemValue(value).toLowerCase().indexOf(this.toLowerCase()) !== -1
      )
    }
    /**
     * @param {IndexedGroupItem<Item>[]} acc
     * @param {IndexedGroupItem<Item>} param1
     * @returns {IndexedGroupItem<Item>[]}
     */
    function groupReducer (acc, { group, index, label }) {
      const items = group.filter(itemFilter, filter)
      return items.length ? acc.concat({ group: items, index, label }) : acc
    }
  }, [filter, withGrouping, indexedSuggestions, getItemValue])
  const selectedIndices = useMemo(
    () => mapIndices(suggestions, selectedItemsIndices, withGrouping),
    [withGrouping, suggestions, selectedItemsIndices]
  )
  const disabledIndices = useMemo(
    () => mapIndices(suggestions, disabledItemsIndices, withGrouping),
    [withGrouping, suggestions, disabledItemsIndices]
  )
  /** @type {(item: IndexedItem<Item>) => JSX.Element} */
  const renderIndexedItem = useCallback(
    (item, ...args) => renderItem(item.value, ...args),
    [renderItem]
  )
  /** @type {(newSelectedIndices: number[], ...args: any) => void} */
  const handleSelectedItemsChanged = useCallback(
    (newSelectedIndices, ...args) => {
      const realSelectedIndices = getSelectedIndices(
        suggestions,
        newSelectedIndices,
        selectedItemsIndices,
        withGrouping
      )
      if (!propsSelectedItemsIndices) setSelectedItemsIndices(realSelectedIndices)
      onSelectedItemsChanged(realSelectedIndices, ...args)
    },
    [
      onSelectedItemsChanged,
      propsSelectedItemsIndices,
      selectedItemsIndices,
      suggestions,
      withGrouping
    ]
  )

  useEffect(() => {
    if (propsSelectedItemsIndices) {
      setSelectedItemsIndices(propsSelectedItemsIndices)
    }
  }, [propsSelectedItemsIndices])

  return {
    data: suggestions,
    disabledItemsIndices: disabledIndices,
    getGroupChildren: getIndexedGroupChildren,
    onSelectedItemsChanged: handleSelectedItemsChanged,
    renderGroupLabel: renderIndexedGroupLabel,
    renderItem: renderIndexedItem,
    selectedItemsIndices: selectedIndices
  }
}

/**
 * Add index to each unpacked suggestion.
 *
 * @template Item
 * @param {UnpackedGroupItem<Item>[]|Item[]} unpackedSuggestions
 * @param {boolean} withGrouping
 * @returns {IndexedItem<Item>[]|IndexedGroupItem<Item>[]}
 */
function getIndexedUnpackedSuggestions (unpackedSuggestions, withGrouping) {
  let indexOffset = 0
  return unpackedSuggestions.map(
    withGrouping ? groupIndexSuggestion : indexSuggestion,
    0
  )

  /**
   * @this {number} Index offset
   * @param {Item} value
   * @param {number} index
   * @returns {IndexedItem}
   */
  function indexSuggestion (value, index) {
    return { value, index: index + this }
  }

  /**
   * @param {UnpackedGroupItem<Item>} param0
   * @param {number} index
   * @returns {IndexedGroupItem<Item>}
   */
  function groupIndexSuggestion ({ group, label }, index) {
    /**
     * @type {IndexedGroupItem<Item>}
     */
    const indexedGroup = {
      label,
      index,
      group: group.map(indexSuggestion, indexOffset)
    }
    indexOffset += group.length
    return indexedGroup
  }
}

/**
 * @template Item
 * @param {IndexedGroupItem<Item>} group
 * @returns {IndexedItem<Item>[]}
 */
function getIndexedGroupChildren (group) {
  return group.group
}

/**
 * @template Item
 * @param {IndexedGroupItem<Item>} group
 * @returns {JSX.Element}
 */
function renderIndexedGroupLabel (group) {
  return group.label
}

/**
 * Map original indices to filtered indices.
 *
 * @template Item
 * @param {IndexedItem<Item>[]|IndexedGroupItem<Item>[]} indexedSuggestions
 * @param {number} indices
 * @param {boolean} withGrouping
 * @returns {number[]}
 */
function mapIndices (indexedSuggestions, indices, withGrouping) {
  const mappedIndices = []
  let indexOffset = 0
  for (let i = 0; i < indexedSuggestions.length; i++) {
    if (withGrouping) {
      const { group } = indexedSuggestions[i]
      for (let j = 0; j < group.length; j++) {
        const { index } = group[j]
        if (indices.indexOf(index) !== -1) mappedIndices.push(indexOffset)
        indexOffset++
      }
    } else {
      const { index } = indexedSuggestions[i]
      if (indices.indexOf(index) !== -1) mappedIndices.push(i)
    }
  }
  return mappedIndices
}

/**
 * Get selected index using the original mapping.
 *
 * @template Item
 * @param {IndexedItem<Item>[]|IndexedGroupItem<Item>[]} indexedSuggestions
 * @param {number[]} indices
 * @param {boolean} withGrouping
 * @returns {number[]}
 */
function getSelectedIndex (indexedSuggestions, indices, _, withGrouping) {
  let indexOffset = 0
  for (let i = 0; i < indexedSuggestions.length; i++) {
    if (withGrouping) {
      const { group } = indexedSuggestions[i]
      for (let j = 0; j < group.length; j++) {
        if (indices.indexOf(indexOffset) !== -1) return [group[j].index]
        indexOffset++
      }
    } else {
      if (indices.indexOf(i) !== -1) return [indexedSuggestions[i].index]
    }
  }
  return []
}

/**
 * Get selected indices using the original mapping.
 *
 * @template Item
 * @param {IndexedItem<Item>[]|IndexedGroupItem<Item>[]} indexedSuggestions
 * @param {number[]} indices
 * @param {number[]} originalIndices
 * @param {boolean} withGrouping
 * @returns {number[]}
 */
function getMultipleSelectedIndices (
  indexedSuggestions,
  indices,
  originalIndices,
  withGrouping
) {
  const mappedIndices = []
  const allIndices = []
  let indexOffset = 0
  for (let i = 0; i < indexedSuggestions.length; i++) {
    if (withGrouping) {
      const { group } = indexedSuggestions[i]
      for (let j = 0; j < group.length; j++) {
        const { index } = group[j]
        if (indices.indexOf(indexOffset) !== -1) mappedIndices.push(index)
        allIndices.push(index)
        indexOffset++
      }
    } else {
      const { index } = indexedSuggestions[i]
      if (indices.indexOf(i) !== -1) mappedIndices.push(index)
      allIndices.push(index)
    }
  }
  for (let i = 0; i < originalIndices.length; i++) {
    const index = originalIndices[i]
    if (allIndices.indexOf(index) === -1) mappedIndices.push(index)
  }
  return mappedIndices.sort((a, b) => a - b)
}

/**
 * @template Item
 * @typedef {Object} UseFilteredDropdownListProps
 * @property {GroupItem<Item>[] | Item[]} data
 * @property {number[]=} defaultSelectedItemsIndices
 * @property {number[]=} disabledItemsIndices
 * @property {string} filter
 * @property {(value: GroupItem<Item>, index?: number) => Item[]} getGroupChildren
 * @property {(value: Item) => string} getItemValue
 * @property {(selectedIndices: number[], ...args: any) => void} onSelectedItemsChanged
 * @property {(value: GroupItem<Item>, index: number) => JSX.Element} renderGroupLabel
 * @property {(value: Item) => JSX.Element} renderItem
 * @property {number[]=} selectedItemsIndices
 * @property {boolean} withGrouping
 */

/**
 * @template Item
 * @typedef {Object} GroupItem
 * @property {Item[]=} group
 */

/**
 * @template Item
 * @typedef {Object} UnpackedGroupItem
 * @property {Item[]} group
 * @property {JSX.Element} label
 */

/**
 * @template Item
 * @typedef {Object} IndexedGroupItem
 * @property {number} index
 * @property {IndexedItem<Item>[]} group
 * @property {JSX.Element} label
 */

/**
 * @template Item
 * @typedef {Object} IndexedItem
 * @property {number} index
 * @property {Item} value
 */
